import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Save, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { Card, Loading } from "../components/UI";
import { formatApiError } from "../lib/format";

export default function PromoBanner() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(r => { setS(r.data); setLoading(false); });
  }, []);

  const update = (k, v) => setS(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/settings/promo-banner", {
        promo_banner_active: !!s.promo_banner_active,
        promo_banner_title: s.promo_banner_title || "",
        promo_banner_body: s.promo_banner_body || "",
        promo_banner_cta_label: s.promo_banner_cta_label || "",
        promo_banner_cta_url: s.promo_banner_cta_url || "",
      });
      setS(r.data);
      toast.success(s.promo_banner_active ? "Banner is now LIVE on modavhomes.com" : "Banner updated (currently inactive)");
    } catch (err) { toast.error(formatApiError(err) || "Save failed"); }
    finally { setSaving(false); }
  };

  if (loading || !s) return <Loading/>;

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="fade-up flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#1A2E24]">Public Promo Banner</h1>
          <p className="text-[#5E7166] text-sm">Advertise a unit or promotion at the top of <a href="https://modavhomes.com" target="_blank" rel="noreferrer" className="text-[#0E5C3A] hover:underline inline-flex items-center gap-1">modavhomes.com <ExternalLink size={11}/></a>. Toggle on/off anytime.</p>
        </div>
        <button onClick={save} disabled={saving} className="btn-gold" data-testid="promo-save-btn">
          <Save size={14}/> {saving ? "Saving…" : "Save Banner"}
        </button>
      </div>

      <Card title="Live Preview" testid="promo-preview">
        <div className="rounded-lg overflow-hidden border border-[#E5EBE8]">
          <div className={s.promo_banner_active ? "bg-[#0E5C3A] text-white" : "bg-[#1A2E24]/40 text-white"} data-testid="promo-preview-bar">
            <div className="px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <Sparkles size={14} className="text-[#C9A227] shrink-0"/>
                <span className="font-bold truncate">{s.promo_banner_title || "Your banner title shows here"}</span>
                {s.promo_banner_body && <span className="text-white/85 hidden sm:inline truncate">— {s.promo_banner_body}</span>}
              </div>
              {s.promo_banner_cta_url && (
                <span className="bg-[#C9A227] text-[#1A2E24] font-bold px-3 py-1 rounded text-xs shrink-0">
                  {s.promo_banner_cta_label || "Learn more"} →
                </span>
              )}
            </div>
          </div>
          {!s.promo_banner_active && (
            <div className="bg-[#FEF0F0] text-[#C92A2A] text-xs text-center py-1.5 font-semibold">
              Banner is currently INACTIVE — visitors won&apos;t see it
            </div>
          )}
        </div>
      </Card>

      <Card title="Banner Settings" testid="promo-settings">
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-[#F8FBF9] border border-[#E5EBE8] rounded-lg p-3">
            <div>
              <div className="font-semibold text-[#1A2E24] text-sm">Show banner on public landing page</div>
              <div className="text-xs text-[#5E7166]">Toggle off to instantly hide. Re-toggle to re-publish.</div>
            </div>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={!!s.promo_banner_active}
                onChange={(e) => update("promo_banner_active", e.target.checked)}
                className="sr-only peer"
                data-testid="promo-active-toggle"
              />
              <div className="w-12 h-7 bg-[#E5EBE8] peer-checked:bg-[#0E5C3A] rounded-full transition-colors relative">
                <div className={`absolute top-0.5 left-0.5 bg-white w-6 h-6 rounded-full shadow transition-transform ${s.promo_banner_active ? "translate-x-5" : ""}`}></div>
              </div>
            </label>
          </div>

          <Field
            label="Banner Title"
            value={s.promo_banner_title || ""}
            onChange={(v) => update("promo_banner_title", v)}
            placeholder="e.g. Hall available for lease"
            testid="promo-title"
          />

          <Field
            label="Banner Body (short)"
            value={s.promo_banner_body || ""}
            onChange={(v) => update("promo_banner_body", v)}
            placeholder="e.g. Reserve before 31 March for 5% off agreement fee"
            testid="promo-body"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label="CTA Button Label"
              value={s.promo_banner_cta_label || ""}
              onChange={(v) => update("promo_banner_cta_label", v)}
              placeholder="e.g. Enquire now"
              testid="promo-cta-label"
            />
            <Field
              label="CTA Link"
              value={s.promo_banner_cta_url || ""}
              onChange={(v) => update("promo_banner_cta_url", v)}
              placeholder="#contact or full URL"
              testid="promo-cta-url"
            />
          </div>
        </div>
      </Card>

      <Card title="Usage tips">
        <ul className="text-sm text-[#5E7166] space-y-1.5 list-disc list-inside">
          <li>Keep the <b>title</b> under 60 characters — it must read at a glance on mobile.</li>
          <li>Leave <b>CTA link</b> as <code>#contact</code> to scroll to the contact form, or use a full URL like <code>https://wa.me/2348012345678</code> to open WhatsApp.</li>
          <li>Toggle is propagated to <code>modavhomes.com</code> instantly — no redeploy needed.</li>
          <li>Both Admin and Property Manager can edit this banner. Every change is audit-logged.</li>
        </ul>
      </Card>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">{label}</span>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="input-modav mt-1"
        data-testid={testid}
      />
    </label>
  );
}
